const SQRT3 = Math.sqrt(3);
const SQRT12 = Math.sqrt(12);

export const GAS_ANALYTES = {
  H2S: { key: 'H2S', label: 'Sulfuro de hidrógeno (H₂S)', shortLabel: 'H₂S', unit: 'ppm', defaultTolerancePercent: 10 },
  CO: { key: 'CO', label: 'Monóxido de carbono (CO)', shortLabel: 'CO', unit: 'ppm', defaultTolerancePercent: 10 },
  O2: { key: 'O2', label: 'Oxígeno (O₂)', shortLabel: 'O₂', unit: '%vol', defaultTolerancePercent: 10 },
  LEL: { key: 'LEL', label: 'Gas combustible', shortLabel: 'LEL', unit: '%LEL', defaultTolerancePercent: 10 },
  SO2: { key: 'SO2', label: 'Dióxido de azufre (SO₂)', shortLabel: 'SO₂', unit: 'ppm', defaultTolerancePercent: 10 },
  CO2: { key: 'CO2', label: 'Dióxido de carbono (CO₂)', shortLabel: 'CO₂', unit: '%vol', defaultTolerancePercent: 10 },
  NH3: { key: 'NH3', label: 'Amoníaco (NH₃)', shortLabel: 'NH₃', unit: 'ppm', defaultTolerancePercent: 10 },
  H2: { key: 'H2', label: 'Hidrógeno (H₂)', shortLabel: 'H₂', unit: 'ppm', defaultTolerancePercent: 10 },
  HCN: { key: 'HCN', label: 'Cianuro de hidrógeno (HCN)', shortLabel: 'HCN', unit: 'ppm', defaultTolerancePercent: 10 },
};

export const GAS_ANALYTE_ORDER = ['H2S', 'CO', 'O2', 'LEL', 'SO2', 'CO2', 'NH3', 'H2', 'HCN'];

export const DEFAULT_GAS_FAMILY_DATA = {
  profile_mode: 'monogas',
  method: 'Comparación directa mediante aplicación de gas patrón certificado al detector.',
  procedure: 'Planillas históricas CALINS para detectores monogás y multigás.',
  repeat_count: 1,
  channels: [{ ...GAS_ANALYTES.H2S, enabled: true, resolution: 1, tolerancePercent: 10 }],
  cylinder_number: '',
  regulator_flow_l_min: 0.5,
  stabilization_seconds: 60,
  zero_test_result: 'not_performed',
  alarm_test_result: 'not_performed',
  response_time_seconds: null,
  decision_rule: 'simple',
  uncertainty_budget: {
    flow_percent: 0,
    temperature_coefficient_percent_per_10c: 0,
    other_percent: 0,
  },
  environment_readings: [
    { key: 'start', label: 'Inicio', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'middle', label: 'Durante', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end', label: 'Fin', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  standard_configuration: {},
  result_details: [],
};

export function finiteOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string' ? value.trim().replace(',', '.') : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export function normalizeAnalyteKey(value) {
  const text = String(value || '').toUpperCase().replace(/₂/g, '2').replace(/₃/g, '3').replace(/[^A-Z0-9]/g, '');
  if (text.includes('H2S') || text.includes('SULFURODEHIDROGENO') || text.includes('HYDROGENSULFIDE')) return 'H2S';
  if (text.includes('SO2') || text.includes('DIOXIDODEAZUFRE') || text.includes('SULFURDIOXIDE')) return 'SO2';
  if (text.includes('CO2') || text.includes('DIOXIDODECARBONO') || text.includes('CARBONDIOXIDE')) return 'CO2';
  if (text === 'CO' || text.includes('MONOXIDODECARBONO') || text.includes('CARBONMONOXIDE')) return 'CO';
  if (text.includes('O2') || text.includes('OXIGENO') || text.includes('OXYGEN')) return 'O2';
  if (text.includes('LEL') || text.includes('LIE') || text.includes('COMBUSTIBLE') || text.includes('METHANE') || text.includes('METANO')) return 'LEL';
  if (text.includes('NH3') || text.includes('AMONIACO') || text.includes('AMMONIA')) return 'NH3';
  if (text.includes('HCN') || text.includes('CIANURO') || text.includes('HYDROGENCYANIDE')) return 'HCN';
  if (text.includes('H2') || text.includes('HIDROGENO') || text.includes('HYDROGEN')) return 'H2';
  return GAS_ANALYTES[text] ? text : '';
}

export function inferGasProfile(instrument = {}, templateCode = '') {
  const text = [instrument.type, instrument.category?.name, instrument.model, templateCode].filter(Boolean).join(' ').toLowerCase();
  const profileMode = text.includes('multi') ? 'multigas' : 'monogas';
  const inferred = GAS_ANALYTE_ORDER.find((key) => normalizeAnalyteKey(text) === key);
  const keys = profileMode === 'multigas' ? ['H2S', 'CO', 'O2', 'LEL'] : [inferred || 'H2S'];
  return {
    code: profileMode === 'multigas' ? 'GAS:multigas:v1' : 'GAS:monogas:v1',
    revision: 'v1',
    title: profileMode === 'multigas' ? 'Detector multigás' : 'Detector monogás',
    profileMode,
    channels: keys.map((key) => ({
      ...GAS_ANALYTES[key],
      enabled: true,
      resolution: finiteOrNull(instrument.resolution) ?? (GAS_ANALYTES[key].unit === 'ppm' ? 1 : 0.1),
      tolerancePercent: finiteOrNull(instrument.tolerance) ?? GAS_ANALYTES[key].defaultTolerancePercent,
    })),
  };
}

function normalizeChannel(value = {}) {
  const key = normalizeAnalyteKey(value.key || value.analyte || value.label) || 'H2S';
  const definition = GAS_ANALYTES[key];
  return {
    ...definition,
    ...value,
    key,
    label: value.label || definition.label,
    shortLabel: value.shortLabel || definition.shortLabel,
    unit: value.unit || definition.unit,
    enabled: value.enabled !== false,
    resolution: finiteOrNull(value.resolution) ?? (definition.unit === 'ppm' ? 1 : 0.1),
    tolerancePercent: Math.abs(finiteOrNull(value.tolerancePercent) ?? definition.defaultTolerancePercent),
  };
}

export function mergeGasFamilyData(value = {}) {
  const input = value && typeof value === 'object' ? value : {};
  const channels = Array.isArray(input.channels) && input.channels.length
    ? input.channels.map(normalizeChannel)
    : DEFAULT_GAS_FAMILY_DATA.channels.map(normalizeChannel);
  const environment = Array.isArray(input.environment_readings)
    ? input.environment_readings
    : DEFAULT_GAS_FAMILY_DATA.environment_readings;
  return {
    ...DEFAULT_GAS_FAMILY_DATA,
    ...input,
    repeat_count: Math.min(3, Math.max(1, Number(input.repeat_count) || 1)),
    channels,
    uncertainty_budget: { ...DEFAULT_GAS_FAMILY_DATA.uncertainty_budget, ...(input.uncertainty_budget || {}) },
    environment_readings: DEFAULT_GAS_FAMILY_DATA.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(environment[index] || {}),
    })),
    standard_configuration: input.standard_configuration || {},
    result_details: Array.isArray(input.result_details) ? input.result_details : [],
  };
}

export function gasBlock() {
  return {
    block_key: 'gas_response',
    display_name: 'Respuesta a gas patrón',
    unit: 'varios',
    applicable: true,
    point_count: 9,
    sort_order: 1,
    mode: 'simple',
  };
}

function mean(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  return valid.length ? valid.reduce((sum, value) => sum + value, 0) / valid.length : null;
}

function sampleStandardDeviation(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (valid.length < 2) return 0;
  const average = mean(valid);
  return Math.sqrt(valid.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (valid.length - 1));
}

function averageTemperature(familyData) {
  return mean((familyData.environment_readings || []).map((row) => row.temp_c));
}

export function findCertifiedGasResult(snapshot, analyteKey) {
  const key = normalizeAnalyteKey(analyteKey);
  return (snapshot?.results || []).find((result) => normalizeAnalyteKey(result?.metadata?.analyte_key || result.range_label) === key) || null;
}

export function computeGasChannelMetrics(row = {}, session = {}, snapshot = null, block = {}) {
  const familyData = mergeGasFamilyData(session.family_data);
  const channel = familyData.channels.find((item) => item.key === row.analyte_key);
  const certified = findCertifiedGasResult(snapshot, row.analyte_key);
  if (!channel || !certified) return null;
  const repeatCount = Math.min(3, Math.max(1, Number(familyData.repeat_count) || 1));
  const readings = Array.from({ length: repeatCount }, (_, index) => finiteOrNull(row[`reading_${index + 1}`])).filter((value) => value !== null);
  if (!readings.length) return null;
  const reference = finiteOrNull(certified.reference_value) ?? finiteOrNull(certified.indicated_value);
  const expandedStandard = Math.abs(finiteOrNull(certified.uncertainty_expanded) ?? 0);
  const standardK = finiteOrNull(certified.coverage_factor) ?? finiteOrNull(snapshot?.certificate?.coverage_factor) ?? 2;
  if (reference === null || expandedStandard <= 0) return null;
  const instrumentMean = mean(readings);
  const errorAbs = instrumentMean - reference;
  const errorPct = reference === 0 ? null : (errorAbs / reference) * 100;
  const repeatability = sampleStandardDeviation(readings);
  const uRepeatability = repeatability / Math.sqrt(Math.max(1, readings.length));
  const uStandard = expandedStandard / (standardK > 0 ? standardK : 2);
  const uResolution = Math.abs(finiteOrNull(channel.resolution) ?? 0) / SQRT12;
  const budget = familyData.uncertainty_budget || {};
  const uFlow = Math.abs(reference) * (Math.abs(finiteOrNull(budget.flow_percent) ?? 0) / 100) / SQRT3;
  const averageTemp = averageTemperature(familyData);
  const tempDelta = averageTemp === null ? 0 : Math.abs(averageTemp - 20);
  const uTemp = Math.abs(reference) * (Math.abs(finiteOrNull(budget.temperature_coefficient_percent_per_10c) ?? 0) / 100) * (tempDelta / 10) / SQRT3;
  const uOther = Math.abs(reference) * (Math.abs(finiteOrNull(budget.other_percent) ?? 0) / 100) / SQRT3;
  const uc = Math.hypot(uStandard, uResolution, uRepeatability, uFlow, uTemp, uOther);
  const k = 2;
  const expanded = uc * k;
  const tolerancePercent = Math.abs(finiteOrNull(channel.tolerancePercent) ?? 10);
  const toleranceAbs = Math.abs(reference) * tolerancePercent / 100;
  const guard = familyData.decision_rule === 'guard_band' ? expanded : 0;
  const inTolerance = toleranceAbs > 0 ? Math.abs(errorAbs) + guard <= toleranceAbs : null;
  return {
    point_no: Number(row.point_no),
    block_id: block.id || null,
    analyte_key: channel.key,
    analyte_label: channel.shortLabel,
    unit: channel.unit,
    pattern_mean: reference,
    instrument_mean: instrumentMean,
    error_abs: errorAbs,
    correction: -errorAbs,
    error_pct: errorPct,
    repeatability,
    u_standard: uStandard,
    u_resolution: uResolution,
    u_repeatability: uRepeatability,
    u_flow: uFlow,
    u_temp: uTemp,
    u_other: uOther,
    uc,
    k,
    u_expanded: expanded,
    u_reported: Math.max(expanded, Math.abs(Number(channel.resolution) || 0)), // piso por resolucion del instrumento (QA 2026-09-06)
    tolerance_percent: tolerancePercent,
    tolerance_abs: toleranceAbs,
    in_tolerance: inTolerance,
    repeat_count: readings.length,
  };
}

export function validateGasRows(rows = [], familyData = {}) {
  const merged = mergeGasFamilyData(familyData);
  const repeatCount = merged.repeat_count;
  const issues = [];
  for (const row of rows) {
    const channel = merged.channels.find((item) => item.key === row.analyte_key);
    if (!channel?.enabled) continue;
    for (let repeat = 1; repeat <= repeatCount; repeat += 1) {
      if (finiteOrNull(row[`reading_${repeat}`]) === null) issues.push(`${channel.shortLabel}: falta la lectura ${repeat}.`);
    }
  }
  if (!String(merged.cylinder_number || '').trim()) issues.push('Falta ingresar el número físico del cilindro utilizado.');
  return issues;
}
